import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { TestTube, BellRinging, Pulse } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  adminInjectEvent, adminInjectAlert,
  adminListPatients, adminListDevices, formatAdminError,
} from '../../lib/admin';
import AdminPage from '../../components/admin/AdminPage';
import { Field, inputCls, selectCls } from '../../components/admin/Field';

const EVENT_TYPES = ['fall', 'sos', 'button_press', 'door_open', 'low_battery', 'offline'];
const SEVERITIES = ['info', 'warning', 'critical'];

export default function AdminQA() {
  const { t } = useI18n();
  const { user } = useAuth();
  const allowed = user?.role === 'admin';

  const [patients, setPatients] = useState([]);
  const [devices, setDevices] = useState([]);

  useEffect(() => {
    if (!allowed) return;
    adminListPatients().then(({ items }) => setPatients(items)).catch(() => setPatients([]));
    adminListDevices().then(({ items }) => setDevices(items)).catch(() => setDevices([]));
  }, [allowed]);

  if (!allowed) {
    return (
      <AdminPage title={t('admin_qa_title')}>
        <div className="bg-bg-alt rounded-xl p-8 text-center text-ink-soft">{t('admin_admin_only')}</div>
      </AdminPage>
    );
  }

  return (
    <AdminPage title={t('admin_qa_title')} description={t('admin_qa_sub')}>
      <div className="grid lg:grid-cols-2 gap-6">
        <InjectorCard
          icon={Pulse}
          title={t('admin_qa_event_title')}
          description={t('admin_qa_event_sub')}
          patients={patients}
          devices={devices}
          fields={[
            { key: 'type', label: t('admin_field_type'), kind: 'select', options: EVENT_TYPES, default: 'fall' },
            { key: 'severity', label: t('admin_field_severity'), kind: 'select', options: SEVERITIES, default: 'critical' },
            { key: 'patient_id', label: t('admin_field_patient'), kind: 'patient' },
            { key: 'device_id', label: t('admin_field_device'), kind: 'device', optional: true },
            { key: 'message', label: t('admin_field_message'), kind: 'text', placeholder: 'Test fall in living room' },
          ]}
          submit={(body) => adminInjectEvent(body)}
          successKey="admin_qa_event_done"
          actionLabel={t('admin_qa_inject_event')}
        />

        <InjectorCard
          icon={BellRinging}
          title={t('admin_qa_alert_title')}
          description={t('admin_qa_alert_sub')}
          patients={patients}
          devices={devices}
          fields={[
            { key: 'severity', label: t('admin_field_severity'), kind: 'select', options: SEVERITIES, default: 'critical' },
            { key: 'patient_id', label: t('admin_field_patient'), kind: 'patient' },
            { key: 'message', label: t('admin_field_message'), kind: 'text', placeholder: 'Manual test alert' },
          ]}
          submit={(body) => adminInjectAlert(body)}
          successKey="admin_qa_alert_done"
          actionLabel={t('admin_qa_inject_alert')}
        />
      </div>

      <div className="mt-6 text-xs text-ink-muted bg-bg-alt rounded-xl p-4">
        <TestTube size={14} className="inline mr-1.5 -mt-0.5" weight="duotone" />
        {t('admin_qa_disclaimer')}
      </div>
    </AdminPage>
  );
}

function InjectorCard({ icon: Icon, title, description, fields, patients, devices, submit, successKey, actionLabel }) {
  const { t } = useI18n();
  const [form, setForm] = useState(() => Object.fromEntries(fields.map((f) => [f.key, f.default || ''])));
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '' && v != null));
      await submit(body);
      toast.success(t(successKey));
    } catch (err) {
      toast.error(formatAdminError(actionLabel, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="bg-white border border-stone_line rounded-2xl shadow-soft p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-sage-100 text-sage-600 grid place-items-center">
          <Icon size={20} weight="duotone" />
        </div>
        <div>
          <h2 className="font-heading text-xl text-ink">{title}</h2>
          {description && <p className="text-xs text-ink-muted mt-0.5">{description}</p>}
        </div>
      </div>

      <div className="space-y-3">
        {fields.map((f) => (
          <Field key={f.key} label={f.label} required={!f.optional}>
            {f.kind === 'select' && (
              <select value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={selectCls}>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {f.kind === 'patient' && (
              <select value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={selectCls}>
                <option value="">—</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            {f.kind === 'device' && (
              <select value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={selectCls}>
                <option value="">—</option>
                {devices.map((d) => <option key={d.id} value={d.id}>{d.mac || d.id} ({d.type})</option>)}
              </select>
            )}
            {f.kind === 'text' && (
              <input type="text" value={form[f.key]} placeholder={f.placeholder}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} />
            )}
          </Field>
        ))}
      </div>

      <button type="submit" disabled={busy}
        className="w-full px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50">
        {busy ? '…' : actionLabel}
      </button>
    </form>
  );
}
